import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { CancellationIneligibilityReason } from '@acorn-portal/shared/src/services/cancellation-api';
import {
	getCancellationEligibility,
	resetEligibility,
	updateStepCeiling,
} from '@acorn-portal/shared/src/store/cancellation/cancellation.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { safelyConsoleError } from '@acorn-portal/shared/src/utils/safely-console-error';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

export type CancellationEligibilityManager = {
	isLoading: boolean;
	hasError: boolean;
	handleClick: () => Promise<void>;
	resetError: () => void;
};

/**
 * Custom hook to manage cancellation eligibility checks
 * It provides a function to trigger the check plus the associated loading and error states
 * @returns An object containing:
 * - handleClick: function to trigger the eligibility check; navigates to the cancellation reason page if eligible; sets error state if there is an issue with this
 * - isLoading: boolean indicating if the check is in progress
 * - hasError: boolean indicating if there was an error
 * - resetError: function to reset the error state
 */
export const useCancellationEligibilityManager = (): CancellationEligibilityManager => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cancellationsState = useAppSelector((state) => state.cancellation);
	// Error state, driven by multiple sources
	const [error, setError] = useState<boolean>(false);
	// Error source 1 - thunk action error
	const errorFromThunk = cancellationsState.eligibilityStatus.error;
	// Error source 2 - backend returned error
	const backendReturnedError =
		cancellationsState.ineligibilityReason === CancellationIneligibilityReason.ErrorCheckingEligibility;

	useEffect(() => {
		if (!!errorFromThunk || !!backendReturnedError) setError(true);
	}, [errorFromThunk, backendReturnedError]);

	/**
	 * Handles the click event for checking cancellation eligibility
	 * Navigates to the cancellation reason page if eligible
	 * Sets error state if there is an issue with the eligibility check
	 */
	const handleClick = async () => {
		try {
			const result = await dispatch(getCancellationEligibility()).unwrap();
			if (result.isEligible) {
				dispatch(updateStepCeiling('reason'));
				navigate(ROUTE_PATHS.cancellationReason);
			}
		} catch (error) {
			// Error source 3 - any other FE issue
			safelyConsoleError('Caught error in handle cancel button click', error);
			setError(true);
		}
	};

	/**
	 * Resets the error state via
	 * - isEligible: true
	 * - ineligibilityReason: undefined
	 * - eligibilityStatus: initial
	 *
	 * Suggested use - closing an error modal that pops up on eligibility check failure
	 */
	const resetError = () => {
		dispatch(resetEligibility());
		setError(false);
	};

	return {
		handleClick,
		isLoading: cancellationsState.eligibilityStatus.isLoading,
		hasError: error,
		resetError,
	};
};
